import {ChartOutput, ChartQuery, ChartSettings, DataLabelPosition} from 'entities/chart';
import {
    aggregationChoicesFilters,
    ChartType,
    EventFilter,
} from '@/views/storyboard/utils/chartConstants';
import {getDefaultDataLabelPosition} from '@/views/storyboard/utils/chartDataLabels';
import {CHART_SETTINGS_FIELD_DEFAULTS} from '@/views/storyboard/utils/chartSettingsDefaults';
import {
    showsAnalysisSection,
    showsBarOrientation,
    showsBarSortOrderSetting,
    showsBignumberOptionsSection,
    showsComparablePeriodInAnalysis,
    showsDataLabelsSetting,
    showsMovingAverageOptions,
    showsScalingSetting,
} from '@/views/storyboard/utils/chartSettingsFieldVisibility';
import {
    cleanupMetricQueryFields,
    cleanupTypeSpecificQueryFields,
    resolveOutputForChartType,
    sanitizeChartQueryForRequest,
} from '@/views/storyboard/utils/sanitizeChartQuery';

const SINGLE_AGGS_ITEM_OUTPUTS: ChartOutput[] = [
    ChartOutput.Terms,
    ChartOutput.TermsTimeline,
    ChartOutput.MetricTimeline,
    ChartOutput.TermsMetric,
    ChartOutput.PivotTerms,
];

export function resetAggsItemForOutput(
    output: ChartOutput | undefined,
    currentAggsItem: string | null,
    eventFilters: EventFilter[],
): string | null {
    if (!output || !SINGLE_AGGS_ITEM_OUTPUTS.includes(output)) {
        return null;
    }

    const availableOptions = aggregationChoicesFilters[output](eventFilters)
        .filter((option) => option[4] === true);
    const currentIsValid = !!currentAggsItem
        && availableOptions.some((option) => option[0] === currentAggsItem);

    if (currentIsValid) {
        return currentAggsItem;
    }

    return availableOptions[0]?.[0] ?? '';
}

export function cleanupChartSettingsForTypeAndOutput(
    chartSettings: ChartSettings,
    chartType: ChartType,
    output: ChartOutput | undefined,
): ChartSettings {
    const defaults = CHART_SETTINGS_FIELD_DEFAULTS;
    const settings: ChartSettings = {...chartSettings, type: chartType};

    if (!showsDataLabelsSetting(chartType, output)) {
        settings.dataLabelPosition = defaults.dataLabelPosition;
    } else if (
        chartType === ChartType.PieChart
        && settings.dataLabelPosition === DataLabelPosition.Off
    ) {
        settings.dataLabelPosition = getDefaultDataLabelPosition(ChartType.PieChart);
    }

    if (!showsBarOrientation(chartType)) {
        settings.barOrientation = defaults.barOrientation;
    }

    if (!showsBarSortOrderSetting(chartType, output)) {
        settings.barSortOrder = defaults.barSortOrder;
    }

    if (!showsAnalysisSection(chartType, output)) {
        settings.linearRegression = defaults.linearRegression;
        settings.movingAverageDays = defaults.movingAverageDays;
        settings.movingAverageDaysCenter = defaults.movingAverageDaysCenter;
    } else if (!showsMovingAverageOptions(chartType, output, null)) {
        settings.movingAverageDays = defaults.movingAverageDays;
        settings.movingAverageDaysCenter = defaults.movingAverageDaysCenter;
    }

    const usesComparablePeriod = showsBignumberOptionsSection(chartType)
        || (showsAnalysisSection(chartType, output) && showsComparablePeriodInAnalysis(output));

    if (!usesComparablePeriod) {
        settings.comparablePeriod = defaults.comparablePeriod;
    }

    if (!showsScalingSetting(chartType, output)) {
        settings.scalingFactor = defaults.scalingFactor;
    }

    return settings;
}

function applyQueryShapeChange(
    chartType: ChartType,
    chartQuery: ChartQuery,
    eventFilters: EventFilter[],
): ChartQuery {
    let query: ChartQuery = {
        ...chartQuery,
        output: resolveOutputForChartType(chartType, chartQuery.output),
    };

    query = cleanupTypeSpecificQueryFields(query, chartType);
    query = cleanupMetricQueryFields(query, chartType);
    query = {
        ...query,
        aggs_item: resetAggsItemForOutput(query.output, query.aggs_item, eventFilters),
    };

    return sanitizeChartQueryForRequest(query, chartType);
}

export function applyChartTypeChange(
    nextType: ChartType,
    chartQuery: ChartQuery,
    chartSettings: ChartSettings,
    eventFilters: EventFilter[] = [],
): {chartQuery: ChartQuery; chartSettings: ChartSettings} {
    const nextQuery = applyQueryShapeChange(nextType, chartQuery, eventFilters);
    const nextSettings = cleanupChartSettingsForTypeAndOutput(
        chartSettings,
        nextType,
        nextQuery.output,
    );

    return {
        chartQuery: nextQuery,
        chartSettings: nextSettings,
    };
}

export function applyOutputChange(
    chartType: ChartType,
    nextOutput: ChartOutput,
    chartQuery: ChartQuery,
    chartSettings: ChartSettings,
    eventFilters: EventFilter[] = [],
): {chartQuery: ChartQuery; chartSettings: ChartSettings} {
    let query: ChartQuery = {
        ...chartQuery,
        output: nextOutput,
    };

    query = cleanupTypeSpecificQueryFields(query, chartType);
    query = cleanupMetricQueryFields(query, chartType);
    query = {
        ...query,
        aggs_item: resetAggsItemForOutput(nextOutput, query.aggs_item, eventFilters),
    };
    query = sanitizeChartQueryForRequest(query, chartType);

    const nextSettings = cleanupChartSettingsForTypeAndOutput(
        chartSettings,
        chartType,
        nextOutput,
    );

    return {
        chartQuery: query,
        chartSettings: nextSettings,
    };
}
